import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Section, Chapter, Reveal } from "./Section";
import { HandHeart, QrCode, X, ScanLine } from "lucide-react";

const METHODS = [
  { id: "paypal", name: "PayPal", handle: "Terrell Turner Ministries", src: "/assets/qr-3.webp", note: "Scan. Pay. Go." },
  { id: "venmo", name: "Venmo", handle: "@Prophettt", src: "/assets/qr-2.webp", note: "Scan with the Venmo app" },
  { id: "cashapp", name: "Cash App", handle: "Terrell Turner Ministries", src: "/assets/qr-1.jpeg", note: "Scan with the Cash App" },
];

export default function Give() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active) document.documentElement.style.overflow = "hidden";
    else document.documentElement.style.overflow = "";
    return () => { document.documentElement.style.overflow = ""; };
  }, [active]);

  return (
    <Section id="give" testid="give-section">
      <Chapter number="09" label="Partner With the Ministry" testid="give-chapter"
        title={<>Ways to <span className="text-vg">Sow a Seed</span></>}
        sub="Your generosity fuels prophetic ministry, leadership development, wellness outreach, and Kingdom advancement around the world. Every seed makes a difference." />

      <div className="mt-14 grid sm:grid-cols-3 gap-6 max-w-5xl">
        {METHODS.map((m, i) => (
          <Reveal key={m.id} delay={0.08 * i}>
            <button data-testid={`give-card-${m.id}`} onClick={() => setActive(m)}
              className="group card-hover glass rounded-2xl p-6 w-full text-left cursor-pointer">
              <div className="relative overflow-hidden rounded-xl bg-white p-4">
                <img src={m.src} alt={`${m.name} QR code — ${m.handle}`} className="w-full aspect-square object-contain" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-colors duration-300">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-violet-500 text-white text-xs font-bold uppercase tracking-[0.15em] px-4 py-2">
                    <ScanLine size={14} /> Tap to Enlarge
                  </span>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/25 to-amber-500/25 border border-white/10 text-amber-300">
                  <QrCode size={17} />
                </span>
                <div>
                  <p className="font-display text-lg font-bold text-white">{m.name}</p>
                  <p className="text-sm text-slate-300">{m.handle}</p>
                </div>
              </div>
            </button>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.3} className="mt-8 flex items-center gap-3 text-base text-slate-300 max-w-5xl">
        <HandHeart size={16} className="shrink-0 text-amber-400" />
        <span>Tap any QR code to enlarge it, then scan with your phone&rsquo;s camera or payment app.</span>
      </Reveal>

      {createPortal(
        <AnimatePresence>
        {active && (
          <motion.div data-testid="give-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }} onClick={() => setActive(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md px-5">
            <motion.div initial={{ scale: 0.85, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
              className="relative w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 shadow-[0_30px_100px_-20px_rgba(139,92,246,0.5)]">
              <button data-testid="give-modal-close" onClick={() => setActive(null)} aria-label="Close"
                className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                <X size={17} />
              </button>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-violet-600">{active.name}</p>
              <p className="mt-1 font-display text-2xl font-extrabold text-slate-900">Sow via {active.name}</p>
              <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                <img src={active.src} alt={`${active.name} QR code — ${active.handle}`} data-testid="give-modal-image"
                  className="w-full max-h-[55vh] object-contain" />
              </div>
              <p className="mt-4 text-center text-sm font-semibold text-slate-700">{active.handle}</p>
              <p className="mt-1 text-center text-sm text-slate-600">{active.note} — point your camera at the code.</p>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </Section>
  );
}
